/* @HEADER
 * @version 1.1.1478 | 2026-11-02
 * @purpose Project switcher — logo + project name, dropdown for create/rename/delete/switch projects. Supports emoji icons, drag reorder, hide/show, and (ZVX-DEV-2692) assigning/moving a project to an organization the user belongs to.
 * @sidecar ProjectSwitcher.tsx.header.md
 * @layer app | @hex adapter | @ctx project
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8269, ZVX-DEV-7717, ZVX-DEV-7667, ZVX-DEV-6461, ZVX-DEV-6381, ZVX-DEV-6252, ZVX-DEV-5441, ZVX-DEV-5092, ZVX-E2E-002

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import {
  getAllProjects,
  getActiveProjectId,
  setActiveProjectId,
  createProject,
  renameProject,
  deleteProject,
  updateProject,
  reorderProjects,
  buildProjectOrgOptions,
  isValidProjectCompanyId,
  resolveProjectPrefix,
  replaceAllProjects,
  type ProjectRecord,
} from '../lib/projectStore'
import { setDbName, resolveDbName } from '../lib/db'
import { readProjectHideFromAggregates, writeProjectHideFromAggregates } from '../lib/db/projectHideFromAggregates'
import { ensureDefaultBoardGroup, ensureDefaultBoard, ensureAllTasksSpace, ensureAllTasksDefaultBoard } from '../lib/db'
import { useMessages } from '../shared/i18n/useMessages'
import { projectRowFacts } from '../lib/projectRowFacts'
import { countBoardsOnDevice } from '../lib/db/projectBoardsOnDevice'
import { resolveProjectListReadiness } from '../lib/projectListReadiness'
import { resolveUnclaimedProjects, applyOwnershipAnswer } from '../lib/localProjectOwnership'
import { hasFirstRegistrySyncSettled, PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT } from '../lib/projectRegistryBridge'
import { resolveStoredAuthUserId } from '../auth/resolveStoredAuthUserId'
import { readAccountIdentity } from '../features/account/public-api'
import { organizationChoiceFields, projectOrgBadgeId, projectOrgChoiceId } from '../lib/projectOrganizationStamp.ts'
import { resolveWorkspaceDisplayName } from '../features/workspace/resolveWorkspaceDisplayName'
import { resolvePersonalWorkspaceId, filterProjectsByOrg } from '../lib/projectOrgScope'
import { readActiveWorkspaceIdFromStorage } from '../features/auth/readActiveWorkspaceId'
import { chooseProjectForWorkspace } from '../lib/chooseProjectForWorkspace'
import { PROJECT_REGISTRY_SYNCED_EVENT } from '../lib/projectRegistryBridge.ts'
import { MEMBERSHIPS_REFRESHED_EVENT } from '../auth/membershipsRefreshedEvent.ts'
import { EmojiPicker } from './EmojiPicker'
import { PROJECT_SWITCHER_TESTIDS } from './project-switcher.testids'
import { HelpTooltip } from './HelpTooltip'
import { ProjectOrgConfirmDialog } from './ProjectOrgConfirmDialog'
import { OPEN_PROJECT_BUG_SETTINGS_EVENT } from '@zvenix/tasks'
import { ProjectSettingsPanel } from './ProjectSettingsPanel'
// ZVX-DEV-3734: shared ConfirmDialog replaces the last native
// window.confirm() call site (the other nine were replaced under
// ZVX-DEV-3633, but this one was deliberately skipped to avoid colliding
// with that sweep in the same file).
import { ConfirmDialog } from '../ui/ConfirmDialog'
// ZVX-DEV-2795: project-creation limit gate -- reuses the generic S11
// useResourceLimit() seam + the shared upsell placard, same pattern already
// used for space/board/canvas/seat creation limits. This is a CLIENT-side
// gate only -- project creation is a purely local/offline localStorage
// mutation (projectStore.createProject never makes a network call), so the
// UX-blocking enforcement has to live here. A defense-in-depth server-side
// check also exists on the async registry push (registry-projects.mjs).
import { useEntitlement, useResourceLimit, EntitlementUpsellPlacard } from '../features/entitlement/public-api'
// ZVX-DEV-2745: in-place switch seam — falls back to window.location.reload()
// when no host provides the context (see ProjectSwitchContext.tsx).
import { useProjectSwitch } from './ProjectSwitchContext'
import './ProjectSwitcher.css'

interface ProjectSwitcherProps {
  defaultName: string
  /** Optional a11y label for the trigger button (ZVX-DEV-2744: mobile header chip reuse). */
  ariaLabel?: string
}


/** ZVX-DEV-5092 — asks the switcher to open with the create field focused. */
export const OPEN_PROJECT_CREATE_EVENT = 'zvenix:open-project-create'

export function ProjectSwitcher({ defaultName: _defaultName, ariaLabel }: ProjectSwitcherProps) {
  const m = useMessages()
  const t = m.projectSwitcher
  const switchApi = useProjectSwitch()
  const [projects, setProjects] = useState<ProjectRecord[]>(() => getAllProjects())
  const [activeId, setActiveId] = useState(() => getActiveProjectId())
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editPrefix, setEditPrefix] = useState('')
  // ZVX-DEV-2692: assign/move a project's organization. '' means personal.
  const [editOrgId, setEditOrgId] = useState('')
  // ZVX-DEV-6252 (D1): the project-level «Не выводить в агрегатных досках», read
  // from the project's own database when edit-mode opens; written on save.
  const [editHideFromAggregates, setEditHideFromAggregates] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 })
  const [emojiPickerId, setEmojiPickerId] = useState<string | null>(null)
  // ZVX-DEV-8842 (ZVX-5311 П1) -- the project whose settings panel is open, and where it stands (by the gear that opened it). The gear is kept to give the focus back to.
  const [settingsFor, setSettingsFor] = useState<{ id: string; top: number; left: number } | null>(null)
  const settingsGear = useRef<HTMLElement | null>(null)
  // ZVX-DEV-8897 (ZVX-5206 C2) -- the phone's card sheet asked for the open project's bug settings: where the panel stands (under the trigger; the panel clamps itself into the window). The project is read at render, so it is the one open NOW.
  const [bugSettingsAt, setBugSettingsAt] = useState<{ top: number; left: number } | null>(null)
  /**
   * ZVX-DEV-5423 — where to draw the picker, in viewport coordinates.
   *
   * It used to be drawn INSIDE `.project-switcher__list`, which is a scroll
   * container, at `left: 100%` — i.e. deliberately outside its own box. A
   * scroll container does not let a child hang out of it; it scrolls to it.
   * That is both scrollbars the operator saw, and it is why the picker was
   * clipped out of reach.
   */
  const [emojiPickerAnchor, setEmojiPickerAnchor] = useState<{ top: number; left: number } | null>(null)
  const [dragProjectId, setDragProjectId] = useState<string | null>(null)
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null)
  const [showHidden, setShowHidden] = useState(false)
  // ZVX-DEV-2692: pending org move awaiting in-app confirm-dialog decision
  // (replaces the native window.confirm previously used here).
  const [pendingOrgChange, setPendingOrgChange] = useState<{ id: string; nextOrgId: string } | null>(null)
  // ZVX-DEV-3734: pending project deletion awaiting the in-app ConfirmDialog
  // decision (replaces the native window.confirm previously used here).
  // ConfirmDialog portals to document.body, outside `ref` below — while a
  // deletion is pending, the dropdown's own Escape/click-outside handlers
  // must defer to it, or the dropdown closes underneath the dialog and the
  // confirmation risks being answered by a component that already
  // unmounted (same footgun DiaryDetailPanel's pendingDelete gate guards
  // against under ZVX-DEV-3633).
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  // ZVX-DEV-7667 -- boards this device holds for the project about to be deleted, read when the dialog opens: `undefined`
  // while it is being counted, `null` when the browser cannot say, a number otherwise.
  const [pendingDeleteBoards, setPendingDeleteBoards] = useState<number | null | undefined>(undefined)
  const entitlement = useEntitlement()
  const [limitUpsellOpen, setLimitUpsellOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const editRef = useRef<HTMLInputElement>(null)
  const createRef = useRef<HTMLInputElement>(null)


  // ZVX-DEV-2692: the orgs this user may assign a project into — "Личное"
  // plus exactly the Companies they currently belong to (never an arbitrary
  // id). Re-read on every render like WorkspaceSwitcher does, so a fresh
  // login/membership change is picked up without extra plumbing.
  const identityWorkspaces = useMemo(() => readAccountIdentity().workspaces, [open])
  // ZVX-DEV-5468 -- shown in the ownership question so a person can see WHICH
  // account they are answering for. A question about ownership that does not
  // name the owner is a coin toss.
  const accountEmail = readAccountIdentity().email

  // ZVX-DEV-4757 — the half of ZVX-DEV-2692 that was never built. Assignment
  // shipped and the organization was rendered as a badge; nothing acted on
  // it, so switching organization left the list identical and the operator
  // reported it as projects "not switching". The active workspace is read
  // reactively because WorkspaceSwitcher deliberately does NOT reload the
  // page when it switches — it only writes storage and fires this event.
  const [activeWorkspaceId, setActiveWorkspaceId] = useState<string | null>(
    () => readActiveWorkspaceIdFromStorage(),
  )
  useEffect(() => {
    function onSwitch() { setActiveWorkspaceId(readActiveWorkspaceIdFromStorage()) }
    window.addEventListener('workspace-switched', onSwitch)
    // ZVX-DEV-5092 -- the left-nav empty state has no switcher of its own, and
    // creating a project is the switcher's job. It asks by name rather than by
    // reaching in: the dropdown opens already in create mode, so the reader
    // lands on the field instead of on a list that is empty for them.
    function onCreateRequested() {
      setOpen(true)
      setCreating(true)
    }
    window.addEventListener(OPEN_PROJECT_CREATE_EVENT, onCreateRequested)
    function onBugSettingsRequested() {
      // On a phone the sidebar's switcher is mounted too (display: none); only the one a person can see answers, or two panels open. A browser without `checkVisibility` (and a test without layout) counts as seen.
      if (triggerRef.current?.checkVisibility?.() === false) return
      const box = triggerRef.current?.getBoundingClientRect()
      settingsGear.current = triggerRef.current
      setOpen(true)
      setBugSettingsAt({ top: Math.max(8, (box?.bottom ?? 0) + 4), left: box?.left ?? 8 })
    }
    window.addEventListener(OPEN_PROJECT_BUG_SETTINGS_EVENT, onBugSettingsRequested)
    return () => {
      window.removeEventListener('workspace-switched', onSwitch)
      window.removeEventListener(OPEN_PROJECT_CREATE_EVENT, onCreateRequested)
      window.removeEventListener(OPEN_PROJECT_BUG_SETTINGS_EVENT, onBugSettingsRequested)
    }
  }, [])
  const personalWorkspaceId = useMemo(
    () => resolvePersonalWorkspaceId(identityWorkspaces),
    [identityWorkspaces],
  )
  const scoped = useMemo(
    () => filterProjectsByOrg(projects, activeWorkspaceId, personalWorkspaceId),
    [projects, activeWorkspaceId, personalWorkspaceId],
  )
  const visibleProjects = scoped.visible.filter(p => !p.hidden)
  const hiddenProjects = scoped.visible.filter(p => p.hidden)
  // ZVX-DEV-2795: project-creation limit. The count includes the always-present
  // «Личное» default project (synthesized directly by `getAllProjects()`, never
  // routed through `createProject()`/this gate), so a FREE user with
  // projectLimit=1 is blocked from creating a SECOND project while never being
  // blocked from getting their first one.
  // ZVX-DEV-6112 -- counted INSIDE the organization on screen, hidden rows
  // included. This used to be `projects.length`, every project on the device:
  // the personal «Личное» alone reached FREE's limit of 1, so a FREE
  // organization a person had just claimed could never get its first project
  // -- «Достигнут лимит проектов» on the empty organization, measured solo on
  // the Docker stack (e2e act I, scene 6.2, 2026-09-19), and the «Нет проекта»
  // placard the scope guard then drew was honest. The limit is the plan's per
  // organization; the count must be the same organization's.
  const canCreateProject = useResourceLimit(scoped.visible.length, entitlement.projectLimit)
  // Distinguishes "this organization has nothing yet" from "you have no
  // projects" — two empty lists that look the same and mean the opposite.
  const scopedOutCount = scoped.hiddenByOrg

  /**
   * ZVX-DEV-5473 -- the trigger names the ACTIVE project, and an organization
   * that holds none has none to name.
   *
   * This used to read the UNSCOPED list, so on entering ЛКТ -- an organization
   * the operator had filed nothing into -- it went on naming «Хроноплан», a
   * project of his personal organization. Everything else had already been
   * taught otherwise: `chooseProjectForWorkspace` answers `null` here and says
   * in its own docstring why a foreign project is not an answer, and the left
   * nav renders an empty state (ZVX-DEV-5003/5092). The decision was made and
   * thrown away one line before it was used.
   *
   * `scoped.visible[0]` rather than `projects[0]` for the same reason: the
   * remaining fallback must stay inside the organization on screen. It is
   * reached for a single frame at most -- the effect below then switches to a
   * real member -- and never at all when the organization is empty.
   */
  const active = scoped.visible.find(p => p.id === activeId) ?? scoped.visible[0]

  // Update document title with project name
  useEffect(() => {
    document.title = active ? `Zvenix: ${active.name}` : 'Zvenix'
  }, [active?.name]) // eslint-disable-line react-hooks/exhaustive-deps
  // ZVX-DEV-7717 -- an organisation is named here the way the workspace switcher
  // names it: the personal one is «Личное» (decided by what it is, see
  // resolveWorkspaceDisplayName), never the server's seeded «Personal Workspace».
  const personalLabel = m.workspace.switcher.personalWorkspaceName
  const namedWorkspaces = useMemo(
    () =>
      identityWorkspaces.map((w) => ({
        id: w.id,
        name: resolveWorkspaceDisplayName(w.name, personalLabel, w.anchorType),
      })),
    [identityWorkspaces, personalLabel],
  )
  const orgOptions = useMemo(() => buildProjectOrgOptions(namedWorkspaces), [namedWorkspaces])
  const orgNameById = useMemo(() => new Map(namedWorkspaces.map((w) => [w.id, w.name] as const)), [namedWorkspaces])

  const refresh = useCallback(() => setProjects(getAllProjects()), [])

  // ZVX-DEV-5457 -- ZVX-DEV-5447 hides a project provably belonging to another
  // account, but the marks it reads are written by the registry sync. Between
  // this component rendering and that first pull answering, an unmarked project
  // left by somebody else IS listed, which on a shared machine is a few seconds
  // of the very leak the chain removes. Rather than guess, say what we are
  // doing (ZVX-DEV-5453 decides when that is warranted).
  const [firstSyncSettled, setFirstSyncSettled] = useState(() => hasFirstRegistrySyncSettled())
  useEffect(() => {
    if (firstSyncSettled) return
    function onSettled() {
      setFirstSyncSettled(true)
    }
    // The SETTLED event, not PROJECT_REGISTRY_SYNCED_EVENT: the latter fires
    // only when the registry CHANGED, and a returning device usually syncs to
    // an identical answer — so a wait keyed on it would never end.
    window.addEventListener(PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT, onSettled)
    return () => window.removeEventListener(PROJECT_REGISTRY_FIRST_SYNC_SETTLED_EVENT, onSettled)
  }, [firstSyncSettled])

  const accountId = resolveStoredAuthUserId()
  const readiness = resolveProjectListReadiness({ accountId, firstSyncSettled, projects })

  // ZVX-DEV-5468 -- the one question evidence cannot answer (ZVX-DEV-5464
  // decides who is asked). One at a time: several dialogs stacked on a first
  // launch would be a wall, and each answer re-runs this and offers the next.
  const [postponed, setPostponed] = useState<string[]>([])
  const unclaimed = resolveUnclaimedProjects({ projects, accountId, firstSyncSettled })
  const asking = unclaimed.find((p) => !postponed.includes(p.id)) ?? null

  const answerOwnership = useCallback(
    (projectId: string, answer: 'mine' | 'not-mine') => {
      const next = applyOwnershipAnswer({ projects: getAllProjects(), projectId, accountId, answer })
      replaceAllProjects(next as ProjectRecord[])
      setProjects(getAllProjects())
    },
    [accountId],
  )

  // ZVX-DEV-2720: re-read the local mirror once syncProjectRegistry() (fired
  // from login) finishes reconciling — this is the ONLY way a project
  // discovered on THIS render (org member's first login, second device)
  // reaches the switcher without a manual refresh, since the initial
  // `useState(() => getAllProjects())` above already ran before that async
  // network round-trip completes.
  useEffect(() => {
    function onRegistrySynced() {
      refresh()
    }
    window.addEventListener(PROJECT_REGISTRY_SYNCED_EVENT, onRegistrySynced)
    return () => window.removeEventListener(PROJECT_REGISTRY_SYNCED_EVENT, onRegistrySynced)
  }, [refresh])

  // ZVX-DEV-8269: a person removed from an organisation loses its projects from the list the moment the membership
  // refresh says so. The registry did not change (no PROJECT_REGISTRY_SYNCED_EVENT), and a window standing on another
  // project is not moved, so without this the hidden projects stayed listed until a reload.
  useEffect(() => {
    window.addEventListener(MEMBERSHIPS_REFRESHED_EVENT, refresh)
    return () => window.removeEventListener(MEMBERSHIPS_REFRESHED_EVENT, refresh)
  }, [refresh])

  useEffect(() => {
    if (!open) { setEditingId(null); setCreating(false); setEmojiPickerId(null); setShowHidden(false); setSettingsFor(null); setBugSettingsAt(null) }
  }, [open])

  const panelFor = settingsFor ?? (bugSettingsAt ? { id: activeId, ...bugSettingsAt } : null)

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      // ZVX-DEV-3734: a pending delete's ConfirmDialog portals outside
      // `ref` — defer to it instead of closing the dropdown underneath it.
      if (pendingDeleteId) return
      // ZVX-DEV-5423 — the icon picker portals outside `ref` for the same
      // reason the dialog above does. Without this line the FIRST mousedown
      // inside it closes the dropdown, unmounting the picker before the click
      // that chose an emoji ever lands — which is the very «не получается
      // изменить иконку» this slice is fixing, reintroduced by its own fix.
      if ((e.target as Element | null)?.closest?.('.project-switcher__emoji-picker-portal')) return
      // ZVX-DEV-8842 -- and the settings panel, drawn on the body for the same reason: a mousedown inside it must not close the dropdown that holds its gear.
      if ((e.target as Element | null)?.closest?.('.project-switcher__settings-portal')) return
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    function handleKey(e: KeyboardEvent) {
      if (pendingDeleteId) return
      // ZVX-DEV-8842 -- Escape inside the open settings panel closes the panel, not the dropdown under it.
      if (panelFor) return
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open, pendingDeleteId, panelFor])

  useEffect(() => { if (editingId) editRef.current?.focus() }, [editingId])
  useEffect(() => { if (creating) createRef.current?.focus() }, [creating])

  function updatePos() {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left })
    }
  }

  function handleSwitch(project: ProjectRecord) {
    // ZVX-DEV-2745: keep the trigger label in sync immediately regardless
    // of which path below actually mounts the new project.
    setActiveId(project.id)
    setOpen(false)
    if (switchApi) {
      // In-place SPA switch: the host (AuthenticatedAppHost in App.tsx) owns
      // setActiveProjectId/setDbName/seed + the loading overlay + the keyed
      // remount, so old project data can never leak onto the new screen.
      switchApi.switchProject(project)
      return
    }
    // BBA fallback — legacy full reload path, kept as the safety seam for
    // any render tree that doesn't provide ProjectSwitchContext.
    setActiveProjectId(project.id)
    setDbName(resolveDbName(project)) // ZVX-DEV-3102: deterministic org-project dbName
    Promise.all([ensureDefaultBoardGroup(), ensureDefaultBoard(), ensureAllTasksSpace(), ensureAllTasksDefaultBoard()]).catch(() => {})
    window.location.reload()
  }

  // ZVX-DEV-4971 — an organization switch moves the ACTIVE project too.
  //
  // The list has been scoped by organization since ZVX-DEV-2745, but nothing
  // moved the active project, and the left nav is built from it. Switching
  // organization therefore left the menu showing the previous organization's
  // content — reported by the operator as "switching organizations does not
  // switch the menu".
  //
  // Guarded by a ref rather than by the dependency array: the effect must run
  // on a CHANGE of workspace, never on mount. Running on mount would move a
  // user off the project they deliberately opened, every reload.
  const lastWorkspaceRef = useRef<string | null | undefined>(undefined)
  useEffect(() => {
    const previous = lastWorkspaceRef.current
    lastWorkspaceRef.current = activeWorkspaceId
    if (previous === undefined || previous === activeWorkspaceId) return
    const targetId = chooseProjectForWorkspace({
      projects,
      workspaceId: activeWorkspaceId,
      activeProjectId: activeId,
      personalWorkspaceId,
    })
    // null means the organization holds nothing yet. Staying put is the only
    // honest answer: switching to a foreign project would show the user one
    // organization's content while the switcher names another.
    if (!targetId || targetId === activeId) return
    const target = projects.find((p) => p.id === targetId)
    if (!target) return
    // Deliberately NOT handleSwitch: that closes the dropdown, and an
    // organization switch made FROM the open dropdown must leave it open so
    // the user sees the new organization's projects appear. ZVX-DEV-4757's
    // render test pins exactly that, and calling handleSwitch here broke it.
    setActiveId(target.id)
    if (switchApi) {
      switchApi.switchProject(target)
      return
    }
    setActiveProjectId(target.id)
    setDbName(resolveDbName(target))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleSwitch is
    // stable for this purpose and listing it would re-run the effect on every
    // render, defeating the change guard above.
  }, [activeWorkspaceId, projects, activeId, personalWorkspaceId])

  function handleRename(id: string) {
    const trimmed = editValue.trim()
    if (trimmed) renameProject(id, trimmed)
    const prefixTrimmed = editPrefix.trim().toUpperCase().replace(/[^A-ZА-ЯЁ0-9]/g, '').slice(0, 5)
    if (prefixTrimmed) updateProject(id, { prefix: prefixTrimmed })
    // ZVX-DEV-6252: the flag lives in the project's own database, by name.
    const edited = projects.find(p => p.id === id)
    if (edited) {
      void writeProjectHideFromAggregates(resolveDbName(edited), editHideFromAggregates).catch(err =>
        console.warn('[project-switcher] hide_from_aggregates not written:', err),
      )
    }
    // ZVX-DEV-2692 (UI-ASSIGN/UI-MOVE): only write a companyId change if it
    // actually differs from the project's current org, only when it matches
    // a Company the user actually belongs to, and only after a confirming
    // dialog (move is non-destructive but limits may now differ — D4).
    const current = projects.find(p => p.id === id)
    // ZVX-DEV-5320 -- read the STAMP, not the derived field. They agree today
    // because every writer below sets both; reading the stamp is what makes
    // that the only place they could ever disagree.
    const currentOrgId = current ? projectOrgChoiceId(current, personalWorkspaceId) : ''
    if (editOrgId !== currentOrgId && isValidProjectCompanyId(editOrgId || null, identityWorkspaces)) {
      setPendingOrgChange({ id, nextOrgId: editOrgId })
      // Leave editingId as-is; the confirm dialog decides whether the org
      // change is applied. Name/prefix edits above already committed.
      refresh()
      return
    }
    setEditingId(null)
    refresh()
  }

  function confirmOrgChange() {
    if (!pendingOrgChange) return
    // ZVX-DEV-5320 -- both halves in one write. Setting companyId alone would
    // leave the stamp behind, and the reader above would then answer from a
    // value older than the one beside it — the drift ZVX-DEV-5314 promised
    // could not happen, happening at the first organization assignment.
    updateProject(
      pendingOrgChange.id,
      organizationChoiceFields(
        pendingOrgChange.nextOrgId,
        personalWorkspaceId,
      ),
    )
    setPendingOrgChange(null)
    setEditingId(null)
    refresh()
  }

  function cancelOrgChange() {
    setPendingOrgChange(null)
    setEditingId(null)
  }

  function handleCreate() {
    const trimmed = newName.trim()
    if (!trimmed) { setCreating(false); return }
    // ZVX-DEV-2795: gate BEFORE the create call, same discipline as the
    // seat-limit gate in InvitationsSection.tsx -- never write the row and
    // then react to it being over-cap.
    if (!canCreateProject) {
      setCreating(false)
      setLimitUpsellOpen(true)
      return
    }
    /*
      ZVX-DEV-5681 - the project is created in the organisation the person is
      standing in, not in one they are asked to pick.
      The list of projects is ALREADY filtered by `activeWorkspaceId`, so the
      chooser could put a project somewhere its own creator would not then see
      it. To create in another organisation, switch to it first - the same
      gesture that decides which projects are on screen.
    */
    const currentOrgId = activeWorkspaceId ?? ''
    const companyId =
      currentOrgId && isValidProjectCompanyId(currentOrgId, identityWorkspaces) ? currentOrgId : null
    const project = createProject(trimmed, companyId, personalWorkspaceId)
    refresh()
    setCreating(false)
    setNewName('')
    handleSwitch(project)
  }

  // ZVX-DEV-3734: arms the ConfirmDialog; the destructive delete only runs
  // from confirmDeleteProject(), fired by an explicit click or Enter on the
  // focused confirm button — never on render/mount.
  function handleDelete(id: string) {
    if (id === 'default') return
    const target = projects.find(p => p.id === id)
    setPendingDeleteBoards(undefined)
    setPendingDeleteId(id)
    if (target?.serverProjectId) {
      void countBoardsOnDevice(resolveDbName(target)).then(
        n => setPendingDeleteBoards(n),
        () => setPendingDeleteBoards(null),
      )
    }
  }

  function confirmDeleteProject() {
    if (!pendingDeleteId) return
    const id = pendingDeleteId
    setPendingDeleteId(null)
    deleteProject(id)
    refresh()
    if (activeId === id) {
      const fallback = getAllProjects()[0]
      handleSwitch(fallback)
    }
  }

  function cancelDeleteProject() {
    setPendingDeleteId(null)
  }

  function handleEmojiSelect(projectId: string, emoji: string, color: string) {
    updateProject(projectId, { icon: emoji || undefined, color })
    setEmojiPickerId(null)
    refresh()
  }

  function handleHide(id: string) {
    if (id === activeId) return // active project cannot be hidden
    updateProject(id, { hidden: true })
    refresh()
  }

  function handleUnhide(id: string) {
    updateProject(id, { hidden: false })
    refresh()
  }

  // ── Drag handlers ──
  function handleDragStart(e: React.DragEvent, id: string) {
    setDragProjectId(id)
    e.dataTransfer.effectAllowed = 'move'
  }

  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverProjectId(id)
  }

  function handleDragLeave() {
    setDragOverProjectId(null)
  }

  function handleDrop(e: React.DragEvent, targetId: string) {
    e.preventDefault()
    setDragOverProjectId(null)
    if (!dragProjectId || dragProjectId === targetId) { setDragProjectId(null); return }

    const ids = visibleProjects.map(p => p.id)
    const fromIdx = ids.indexOf(dragProjectId)
    const toIdx = ids.indexOf(targetId)
    if (fromIdx === -1 || toIdx === -1) { setDragProjectId(null); return }

    ids.splice(fromIdx, 1)
    ids.splice(toIdx, 0, dragProjectId)
    // Append hidden project ids at the end to preserve them
    const hiddenIds = hiddenProjects.map(p => p.id)
    reorderProjects([...ids, ...hiddenIds])
    setDragProjectId(null)
    refresh()
  }

  function handleDragEnd() {
    setDragProjectId(null)
    setDragOverProjectId(null)
  }

  function renderProjectLogo(p: ProjectRecord, onClick?: (anchor: { top: number; left: number }) => void) {
    const isImage = p.icon?.startsWith('data:image/')
    return (
      <span
        className="project-switcher__item-logo"
        style={{ background: isImage ? 'transparent' : (p.color || '#3b82f6') }}
        onClick={onClick ? (e) => {
          e.stopPropagation()
          const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
          // Beside the logo, not over it: the same place the absolute rule drew
          // it, now measured against the viewport instead of an ancestor.
          onClick({ top: r.top, left: r.right + 4 })
        } : undefined}
        role={onClick ? 'button' : undefined}
        data-testid={PROJECT_SWITCHER_TESTIDS.rowLogo(p.id)}
      >
        {isImage
          ? <img src={p.icon} alt="" className="project-switcher__item-logo-img" />
          : (p.icon || p.name.charAt(0).toUpperCase())}
      </span>
    )
  }

  return (
    <div className="project-switcher" ref={ref}>
      <button
        ref={triggerRef}
        type="button"
        className="project-switcher__trigger"
        onClick={() => { updatePos(); setOpen(v => !v) }}
        data-testid={PROJECT_SWITCHER_TESTIDS.trigger}
        aria-label={ariaLabel}
        // ZVX-DEV-6556: additive to aria-label, which most mounts leave unset —
        // a sighted user had nothing at all saying what the triangle does.
        title={open ? t.collapseTooltip : t.expandTooltip}
      >
        <span
          className="project-switcher__logo"
          style={{ background: active?.icon?.startsWith('data:image/') ? 'transparent' : (active?.color || undefined) }}
          title={m.projectSwitcher.pickIconTooltip}
          data-testid={PROJECT_SWITCHER_TESTIDS.triggerLogo}
          data-project-icon={active?.icon ?? ''}
        >

          {active?.icon?.startsWith('data:image/')
            ? <img src={active.icon} alt="" className="project-switcher__logo-img" />
            : (active?.icon || (active?.name ?? 'ZX').charAt(0).toUpperCase())}
        </span>
        <span
          className="project-switcher__name"
          data-testid={PROJECT_SWITCHER_TESTIDS.triggerName}
        >{active?.name ?? t.noProject}</span>
        <span className="project-switcher__arrow">{open ? '\u25B4' : '\u25BE'}</span>
      </button>

      {open && (
        <div
          className="project-switcher__dropdown"
          style={{
            top: dropdownPos.top,
            left: dropdownPos.left,
            // ZVX-DEV-3632: bounds the dropdown to whatever room is actually
            // left below the trigger, whatever the window size or however
            // many projects exist. `dropdownPos.top` is only known once
            // `updatePos()` reads the trigger's live getBoundingClientRect()
            // (never a static guess), so the ceiling is expressed as a
            // formula against it rather than a hand-picked pixel constant —
            // see .claude/rules/development.md's CSS layout-constant rule.
            ['--project-switcher-dropdown-max-h' as string]: `calc(100vh - ${dropdownPos.top}px - 16px)`,
          }}
          data-testid={PROJECT_SWITCHER_TESTIDS.dropdown}
        >
          <div className="project-switcher__list" data-testid={PROJECT_SWITCHER_TESTIDS.list}>
          <div className="project-switcher__section-label">{t.projects}</div>

          {/* ZVX-DEV-5457 -- while ownership of at least one project is still
              unknown, show what we are doing instead of a list that may not be
              this account's. */}
          {readiness.state === 'checking' && (
            <div
              className="project-switcher__ownership-check"
              data-testid={PROJECT_SWITCHER_TESTIDS.ownershipCheck}
              role="status"
            >
              <span className="project-switcher__ownership-check-spinner" aria-hidden="true" />
              {t.ownershipCheck}
            </div>
          )}

          {/* ZVX-DEV-4757: an organization the user belongs to but has filed
              nothing into shows an empty list, which is indistinguishable from
              having lost your projects. Say which of the two it is, and how
              many are elsewhere. */}
          {/* ZVX-DEV-5473 -- «когда в организации пусто, тогда в организации
              пусто»: no rows, and in their place the one action that changes
              that. The count still names WHICH empty this is when there are
              projects elsewhere (ZVX-DEV-4757), but the offer no longer
              depends on there being any — a first organization with nothing
              anywhere needs it most. */}
          {visibleProjects.length === 0 && (
            <div
              className="project-switcher__empty-org"
              data-testid={PROJECT_SWITCHER_TESTIDS.emptyOrg}
            >
              {scopedOutCount > 0 && (
                <span className="project-switcher__empty-org-note">{t.emptyOrg(scopedOutCount)}</span>
              )}
              <button
                type="button"
                className="project-switcher__empty-org-create"
                data-testid={PROJECT_SWITCHER_TESTIDS.emptyOrgCreate}
                onClick={() => setCreating(true)}
              >
                {t.otherOrgCreate}
              </button>
            </div>
          )}

          {visibleProjects.map(p => (
            <div
              key={p.id}
              className={`project-switcher__row${dragProjectId === p.id ? ' project-switcher__row--dragging' : ''}${dragOverProjectId === p.id ? ' project-switcher__row--dragover' : ''}`}
              onDragOver={e => handleDragOver(e, p.id)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, p.id)}
            >
              <span
                className="project-switcher__grip"
                draggable
                onDragStart={e => handleDragStart(e, p.id)}
                onDragEnd={handleDragEnd}
              >
                {'\u2807'}
              </span>
              {editingId === p.id ? (
                <div
                  className="project-switcher__edit-row"
                  onBlur={e => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) handleRename(p.id)
                  }}
                >
                  {renderProjectLogo(p, (anchor) => { setEmojiPickerAnchor(anchor); setEmojiPickerId(p.id) })}
                  <input
                    ref={editRef}
                    className="project-switcher__edit-input"
                    data-testid={PROJECT_SWITCHER_TESTIDS.editNameInput(p.id)}
                    value={editValue}
                    onChange={e => setEditValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRename(p.id)
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                  />
                  {/* ZVX-3525 -- the key, the organisation and the checkbox are on a line of their own, so none of
                      them can press the one shrinkable thing on the first line, the name field, to nothing. */}
                  <div className="project-switcher__edit-meta">
                  <input
                    className="project-switcher__prefix-input"
                    data-testid={PROJECT_SWITCHER_TESTIDS.editPrefixInput(p.id)}
                    value={editPrefix}
                    onChange={e => setEditPrefix(e.target.value.toUpperCase().replace(/[^A-ZА-ЯЁ0-9]/g, '').slice(0, 5))}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleRename(p.id)
                      if (e.key === 'Escape') setEditingId(null)
                    }}
                    placeholder="KEY"
                    title={m.projectSwitcher.prefixTooltip}
                    maxLength={5}
                  />
                  {/* ZVX-DEV-7717 -- nobody signed in has no organisation to choose from. */}
                  {orgOptions.length > 0 && (
                    <select
                      className="project-switcher__org-select"
                      data-testid={PROJECT_SWITCHER_TESTIDS.orgSelect(p.id)}
                      value={editOrgId}
                      title={t.orgLabel}
                      onChange={e => setEditOrgId(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleRename(p.id)
                        if (e.key === 'Escape') setEditingId(null)
                      }}
                    >
                      {/* ZVX-DEV-7717 -- a project that belongs to no organisation is not filed
                          under «Личное»: it stands on a line that says so and cannot be chosen. (A project
                        that names an organisation the account does not belong to is not listed at all —
                        projectOrgScope hides it — so no edit row reaches that case.) */}
                      {editOrgId === '' && <option value="" disabled>{t.orgNotChosen}</option>}
                      {orgOptions.map(o => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                      ))}
                    </select>
                  )}
                  {/* ZVX-DEV-6252 (D1): keep the whole project off «Все проекты» and
                      «Все задачи организации»; its own «Все задачи проекта» stays. */}
                  <label className="project-switcher__edit-checkbox" title={t.hideFromAggregatesHint}>
                    <input
                      type="checkbox"
                      data-testid={PROJECT_SWITCHER_TESTIDS.hideFromAggregatesToggle(p.id)}
                      checked={editHideFromAggregates}
                      onChange={e => setEditHideFromAggregates(e.target.checked)}
                    />
                    {t.hideFromAggregates}
                  </label>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className={`project-switcher__item${p.id === activeId ? ' project-switcher__item--active' : ''}`}
                  /* ZVX-DEV-5490 — the row a person clicks to switch project.
                     The registry has DECLARED this id since ZVX-E2E-002 and no
                     row ever carried it, so three walkthrough steps waited for
                     an element that could not appear. Three other files had
                     already written the trap down without closing it. */
                  data-testid={PROJECT_SWITCHER_TESTIDS.projectItem(p.id)}
                  onClick={() => { if (p.id !== activeId) handleSwitch(p) }}
                >
                  {renderProjectLogo(p, (anchor) => { setEmojiPickerAnchor(anchor); setEmojiPickerId(p.id) })}
                  <span className="project-switcher__item-name">{p.name}</span>
                  <HelpTooltip text={t.prefixExplainer}>
                    <span className="project-switcher__prefix-badge" title={m.projectSwitcher.prefixBadgeTooltip} data-testid={PROJECT_SWITCHER_TESTIDS.prefixBadge(p.id)}>{resolveProjectPrefix(p)}</span>
                  </HelpTooltip>
                  {/* ZVX-DEV-5441 — the same question the LIST asks. It filters
                      on `companyId` while this badge read only the stamp, so a
                      project with one and not the other was listed under an
                      organisation and labelled with none — which is what the
                      operator photographed for ЛКТ. */}
                  {(() => {
                    const badgeOrgId = projectOrgBadgeId(p, personalWorkspaceId)
                    if (!badgeOrgId || !orgNameById.has(badgeOrgId)) return null
                    return (
                      <span
                        className="project-switcher__org-badge"
                        data-testid={PROJECT_SWITCHER_TESTIDS.orgBadge(p.id)}
                        title={t.orgLabel}
                      >
                        {orgNameById.get(badgeOrgId)}
                      </span>
                    )
                  })()}
                  {p.id === activeId && <span className="project-switcher__check">{'\u2713'}</span>}
                </button>
              )}
              {editingId !== p.id && <div className="project-switcher__row-actions">
                <button
                  type="button"
                  className="project-switcher__row-btn"
                  data-testid={PROJECT_SWITCHER_TESTIDS.settingsBtn(p.id)}
                  title={t.settingsTip}
                  aria-label={t.settingsTip}
                  aria-haspopup="true"
                  aria-expanded={settingsFor?.id === p.id}
                  onClick={e => {
                    e.stopPropagation()
                    const box = e.currentTarget.getBoundingClientRect()
                    settingsGear.current = e.currentTarget
                    setSettingsFor({ id: p.id, top: Math.max(8, box.top), left: box.right + 8 })
                  }}
                >{'\u2699'}</button>
                <button
                  type="button"
                  className="project-switcher__row-btn"
                  data-testid={PROJECT_SWITCHER_TESTIDS.renameBtn(p.id)}
                  title={t.renameTip}
                  onClick={e => {
                    // ZVX-DEV-6708: the STORED prefix, and an empty field when
                    // there is none. `resolveProjectPrefix` answers about the
                    // account when a project has no prefix of its own, and
                    // `handleRename` writes whatever is in this field as that
                    // project's explicit prefix — so pre-filling the derived
                    // value turned any save into a permanent answer nobody gave.
                    e.stopPropagation(); setEditingId(p.id); setEditValue(p.name); setEditPrefix(p.prefix ?? ''); setEditOrgId(projectOrgChoiceId(p, personalWorkspaceId))
                    // ZVX-DEV-6252: unticked until the project's own database answers.
                    setEditHideFromAggregates(false)
                    void readProjectHideFromAggregates(resolveDbName(p)).then(v => setEditHideFromAggregates(v)).catch(() => {})
                  }}
                >{'\u270E'}</button>
                {p.id !== activeId && (
                  <HelpTooltip text={t.hideExplainer}>
                    <button
                      type="button"
                      className="project-switcher__row-btn"
                      title={t.hideTip}
                      onClick={e => { e.stopPropagation(); handleHide(p.id) }}
                    >{'\u{1F441}'}</button>
                  </HelpTooltip>
                )}
                {p.id !== 'default' && projectRowFacts(p).canDelete && (
                  <button
                    type="button"
                    className="project-switcher__row-btn project-switcher__row-btn--danger"
                    title={t.deleteConfirm}
                    data-testid={PROJECT_SWITCHER_TESTIDS.deleteBtn(p.id)}
                    onClick={e => { e.stopPropagation(); handleDelete(p.id) }}
                  >{'\u2715'}</button>
                )}
              </div>}
            </div>
          ))}

          {hiddenProjects.length > 0 && (
            <>
              <div className="project-switcher__divider" />
              <button
                type="button"
                className="project-switcher__section-label project-switcher__section-label--clickable"
                onClick={() => setShowHidden(v => !v)}
              >
                {t.hidden} ({hiddenProjects.length}) {showHidden ? '\u25B4' : '\u25BE'}
              </button>
              {showHidden && hiddenProjects.map(p => (
                <div key={p.id} className="project-switcher__row project-switcher__row--hidden">
                  <button
                    type="button"
                    className="project-switcher__item project-switcher__item--hidden"
                    title={t.unhideTip}
                    onClick={() => handleUnhide(p.id)}
                  >
                    {renderProjectLogo(p)}
                    <span className="project-switcher__item-name">{p.name}</span>
                  </button>
                </div>
              ))}
            </>
          )}
          </div>

          <div className="project-switcher__divider" />

          {creating ? (
            <div
              className="project-switcher__edit-row"
              onBlur={e => {
                if (e.currentTarget.contains(e.relatedTarget as Node)) return
                if (newName.trim()) handleCreate(); else setCreating(false)
              }}
            >
              <span className="project-switcher__item-icon">+</span>
              <input
                ref={createRef}
                className="project-switcher__edit-input"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCreate()
                  if (e.key === 'Escape') { setCreating(false); setNewName('') }
                }}
                placeholder={t.newProject + '...'}
                data-testid={PROJECT_SWITCHER_TESTIDS.newProjectInput}
              />
              {/*
                ZVX-DEV-5681 - where this project will live, stated rather than
                asked. Removing the control without saying anything would leave
                a person guessing which organisation they are creating in, and
                that is the one thing the old chooser did do well.
              */}
              {activeWorkspaceId && orgNameById.get(activeWorkspaceId) && (
                <span
                  className="project-switcher__org-label"
                  data-testid={PROJECT_SWITCHER_TESTIDS.newProjectOrgLabel}
                  title={t.orgLabel}
                >
                  {orgNameById.get(activeWorkspaceId)}
                </span>
              )}
            </div>
          ) : (
            <button
              type="button"
              className="project-switcher__item project-switcher__item--action"
              onClick={() => { setCreating(true); setNewName('') }}
              data-testid={PROJECT_SWITCHER_TESTIDS.newProjectBtn}
            >
              <span className="project-switcher__item-icon">+</span>
              <span>{t.newProject}</span>
            </button>
          )}
        </div>
      )}

      <ProjectOrgConfirmDialog
        open={pendingOrgChange !== null}
        title={t.orgMoveConfirmTitle}
        body={t.orgMoveConfirm}
        confirmLabel={t.orgMoveConfirmBtn}
        cancelLabel={t.orgMoveCancelBtn}
        onConfirm={confirmOrgChange}
        onCancel={cancelOrgChange}
      />

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title={t.deleteConfirm}
        body={(() => {
          const target = projects.find(p => p.id === pendingDeleteId)
          if (!target) return t.deleteConfirmBody('')
          // A project the server knows is deleted for everyone in its organization; one never pushed is only on this device.
          if (!target.serverProjectId) return t.deleteConfirmBody(target.name)
          return t.deleteServerProjectBody(
            target.name,
            projectRowFacts(target).spaceCount,
            pendingDeleteBoards === undefined ? null : pendingDeleteBoards,
          )
        })()}
        confirmLabel={t.deleteConfirmBtn}
        cancelLabel={m.common.cancelBtn}
        onConfirm={confirmDeleteProject}
        onCancel={cancelDeleteProject}
        testId={PROJECT_SWITCHER_TESTIDS.deleteConfirmDialog}
      />

      {/* ZVX-DEV-5423 — the icon picker, drawn on the body rather than inside
          the scrolling list.

          No `stopPropagation` here, deliberately. React events bubble through
          the FIBER tree, and this portal is mounted at the component ROOT — no
          row's handler is in its ancestry, so there is nothing to stop.
          Measured: removing a guard placed here reddened not one case. What
          DOES need handling is the document-level mousedown above, which is
          DOM-based and does see this. */}
      {emojiPickerId && emojiPickerAnchor && (() => {
        const target = projects.find(p => p.id === emojiPickerId)
        if (!target) return null
        return createPortal(
          <div
            className="project-switcher__emoji-picker-portal"
            style={{ position: 'fixed', top: emojiPickerAnchor.top, left: emojiPickerAnchor.left }}
          >
            <EmojiPicker
              value={target.icon}
              color={target.color}
              onSelect={(emoji, color) => handleEmojiSelect(target.id, emoji, color)}
              onClose={() => setEmojiPickerId(null)}
            />
          </div>,
          document.body,
        )
      })()}

      {/* ZVX-DEV-8842 (ZVX-5311 П1) -- the project's settings panel, drawn on the body like the icon picker above. */}
      {panelFor && (() => {
        const target = projects.find(p => p.id === panelFor.id)
        if (!target) return null
        return (
          <ProjectSettingsPanel
            projectName={target.name}
            isOpenProject={target.id === activeId}
            place={{ top: panelFor.top, left: panelFor.left }}
            onOpenProject={() => handleSwitch(target)}
            onClose={() => { setSettingsFor(null); setBugSettingsAt(null); settingsGear.current?.focus() }}
          />
        )
      })()}

      {limitUpsellOpen && (
        <EntitlementUpsellPlacard
          onClose={() => setLimitUpsellOpen(false)}
          onOpenAccount={() => { window.location.hash = '#account' }}
          title={m.entitlement.limit.project.title}
          body={m.entitlement.limit.project.body}
        />
      )}

      {/* ZVX-DEV-5468 -- the one question no evidence can answer. Rendered
          whether or not the dropdown is open: it is about which data this
          account may see, not about the menu, and hiding it behind a click
          would leave a project of unknown ownership listed until someone
          happened to look.

          Escape and the overlay mean NOT NOW, never «not mine»: hiding a
          person's project by an accidental keystroke is exactly the harm this
          whole chain removes. That is why «Не мой» is the THIRD action rather
          than the cancel — the reason ZVX-DEV-5324 added one. */}
      {asking && (
        <ConfirmDialog
          open
          testId={PROJECT_SWITCHER_TESTIDS.ownershipAsk}
          title={t.ownershipAsk.title}
          body={`${t.ownershipAsk.body.replace('{name}', asking.name)} ${t.ownershipAsk.signedInAs.replace('{account}', accountEmail)}`}
          confirmLabel={t.ownershipAsk.mine}
          destructive={false}
          onConfirm={() => answerOwnership(asking.id, 'mine')}
          thirdActionLabel={t.ownershipAsk.notMine}
          onThirdAction={() => answerOwnership(asking.id, 'not-mine')}
          cancelLabel={t.ownershipAsk.later}
          onCancel={() => setPostponed((ids) => [...ids, asking.id])}
        />
      )}
    </div>
  )
}
